import React from 'react';
import {
  Box, Typography, TextField, InputAdornment, CircularProgress,
  Autocomplete, Chip, alpha, Fade, Paper
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Search, Check } from '@mui/icons-material';

const CourseSelection = ({
  courseOptions,
  searchTerm,
  setSearchTerm,
  loadingOptions,
  selectedCourses,
  handleAddCourse,
  handleRemoveCourse,
  courseAddedAlert
}) => {
  const theme = useTheme();
  
  // Helper text changes based on whether any courses are selected
  const placeholderText = selectedCourses.length > 0 
    ? "Add another golf course..." 
    : "Start typing a golf course name...";
    
  // Same for no options text
  const noOptionsMessage = searchTerm.length < 2 
    ? placeholderText 
    : "No matching courses found";

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 3,
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        mb: 2,
        position: 'relative', // For positioning the overlay notification
      }}
    >
      <Typography
        variant="h6"
        component="h2"
        sx={{
          mb: 1.5,
          fontWeight: 'bold',
          color: theme.palette.primary.dark,
          fontSize: '1.1rem'
        }}
      >
        Where would you like to play?
      </Typography>

      <Box sx={{ position: 'relative' }}>
        <Autocomplete
          id="course-search-autocomplete"
          options={courseOptions}
          getOptionLabel={(option) => `${option.name} (${option.club_name})`}
          filterOptions={(x) => x}
          onInputChange={(event, newInputValue) => {
            setSearchTerm(newInputValue);
          }}
          onChange={(event, value) => {
            handleAddCourse(event, value);
            // Input value is controlled by searchTerm state
            // setSearchTerm is called in handleAddCourse, so no need to clear it here
          }}
          loading={loadingOptions}
          value={null}
          inputValue={searchTerm} // Explicitly control the input value
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText={noOptionsMessage}
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Add Courses to Search"
              variant="outlined"
              placeholder={placeholderText}
              fullWidth
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: '1rem',
                  borderRadius: 2,
                },
                '& .MuiInputLabel-root': {
                  fontSize: '1rem',
                },
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="primary" sx={{ fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {loadingOptions ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        
        {/* Non-disruptive course added notification overlay */}
        <Fade in={Boolean(courseAddedAlert)} timeout={{ enter: 300, exit: 500 }}>
          <Box
            sx={{
              position: 'absolute',
              bottom: '-45px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              pointerEvents: 'none', // Make it non-interactive
              opacity: courseAddedAlert ? 0.95 : 0,
              transition: 'opacity 0.3s ease-in-out',
              width: 'auto',
              minWidth: '180px',
              maxWidth: '80%',
            }}
          >
            {courseAddedAlert && (
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 1, 
                  backgroundColor: theme.palette.success.main,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  justifyContent: 'center'
                }}
              >
                <Check sx={{ color: 'white', fontSize: 18 }} />
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 'medium', fontSize: '0.85rem' }}>
                  <strong>{courseAddedAlert.name}</strong> added!
                </Typography>
              </Paper>
            )}
          </Box>
        </Fade>
      </Box>

      {/* Selected Courses Chips */}
      {selectedCourses.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.8,
            mt: 1.5,
            p: 1,
            borderRadius: 2,
            border: `1px dashed ${theme.palette.primary.main}`,
            backgroundColor: 'alpha(theme.palette.primary.light, 0.08)'
          }}
        >
          {selectedCourses.map((course) => (
            <Chip
              key={course.id}
              label={`${course.name} (${course.club_name})`}
              onDelete={() => handleRemoveCourse(course)}
              color="primary"
              size="small"
              sx={{
                fontSize: '0.8rem',
                fontWeight: 'medium',
                backgroundColor: alpha(theme.palette.primary.main, 0.85),
                color: 'white',
                animation: courseAddedAlert && courseAddedAlert.id === course.id 
                  ? 'pulseChip 1.0s ease-in-out' : 'none',
                '@keyframes pulseChip': {
                  '0%': { boxShadow: '0 0 0 0 rgb(0, 110, 245)' },
                  '70%': { boxShadow: '0 0 0 7px rgba(76, 175, 80, 0)' },
                  '100%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)' }
                },
                '& .MuiChip-deleteIcon': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '16px',
                  marginRight: '1px',
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 1)',
                  }
                }
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default CourseSelection;